<template>
  <div class="heatmap-question">
    <div class="toolbar" v-if="!readonly">
      <div class="tool-group">
        <label>笔触大小:</label>
        <el-slider 
          v-model="radius" 
          :min="10" 
          :max="80" 
          show-tooltip
          @input="drawHeatmap"
          style="width: 100px; display: inline-block; vertical-align: middle; margin-left: 10px;"
        ></el-slider>
      </div>
      <div class="tool-group">
        <el-button size="mini" type="danger" plain icon="el-icon-delete" @click="clearCanvas">清空</el-button>
        <span class="point-count">点数: {{ points.length }}</span>
      </div>
    </div>

    <!-- 画布区域 -->
    <div class="canvas-wrapper" ref="wrapper" :style="{ minHeight: minHeight + 'px' }">
      <!-- 底图 (用于撑开容器和显示背景) -->
      <img ref="baseImage" :src="imageUrl" class="base-image" @load="onImageLoad" crossorigin="anonymous">
      
      <!-- 绘图 Canvas -->
      <canvas 
        ref="canvas"
        class="heatmap-canvas"
        :class="{ 'readonly': readonly }"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
        @touchstart.prevent="startDrawing"
        @touchmove.prevent="draw"
        @touchend="stopDrawing"
      ></canvas>

      <!-- 隐藏的计算 Canvas -->
      <canvas ref="tempCanvas" style="display: none;"></canvas>
      
      <!-- 色谱图例（右上角悬浮，竖向） -->
      <div class="legend" v-if="showLegend">
        <span class="legend-label-top">密集</span>
        <div class="gradient-bar"></div>
        <span class="legend-label-bottom">起笔</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'HeatmapQuestion',
  props: {
    // 题目配置
    question: {
      type: Object,
      required: true
    },
    // 答案值 (JSON字符串 或 对象数组)
    value: {
      type: [String, Array],
      default: () => []
    },
    // 是否只读
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      points: [],        // 存储归一化坐标 [{x, y, t}]
      radius: 30,        // 笔触半径
      opacity: 0.05,     // 叠加透明度/灵敏度
      isDrawing: false,
      paletteData: null, // 色谱数据
      imgLoaded: false,
      canvasWidth: 0,
      canvasHeight: 0,
      minHeight: 300,
      baseImageData: null // 底图像素数据（用于白色区域检测）
    }
  },
  computed: {
    // 解析 configJson（可能是字符串或对象）
    config() {
      const cfg = this.question.config_json || this.question.configJson
      if (!cfg) return {}
      if (typeof cfg === 'string') {
        try {
          return JSON.parse(cfg)
        } catch (e) {
          return {}
        }
      }
      return cfg
    },
    imageUrl() {
      // 优先使用题目配置中的图片，没有则使用默认占位图
      return this.config.imageUrl || this.question.imageUrl || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'800\' height=\'500\' viewBox=\'0 0 800 500\'%3E%3Crect width=\'800\' height=\'500\' fill=\'%23eee\'/%3E%3Ctext x=\'400\' y=\'250\' font-family=\'Arial\' font-size=\'24\' text-anchor=\'middle\' fill=\'%23aaa\'%3E请在管理端配置底图%3C/text%3E%3C/svg%3E'
    },
    // 是否启用白色区域禁止点击
    blockWhite() {
      return this.config.heatmapBlockWhite === true
    },
    showLegend() {
      return this.points.length > 0
    }
  },
  watch: {
    value: {
      handler(val) {
        this.parseValue(val)
      },
      immediate: true
    }
  },
  mounted() {
    this.initPalette()
    // 如果图片已经加载（例如缓存），手动触发
    if (this.$refs.baseImage && this.$refs.baseImage.complete) {
      this.onImageLoad()
    }
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    // 初始化色谱
    initPalette() {
      const pCanvas = document.createElement('canvas')
      pCanvas.width = 1
      pCanvas.height = 256
      const ctx = pCanvas.getContext('2d')
      const grad = ctx.createLinearGradient(0, 0, 0, 256)

      // 0% - 15%: 白色高光 (White Start)
      grad.addColorStop(0.0, 'rgba(255,255,255,0)')
      grad.addColorStop(0.05, '#FFFFFF')
      grad.addColorStop(0.15, '#FFFFE0')

      // 20% - 40%: 黄色
      grad.addColorStop(0.2, '#FFFACD')
      grad.addColorStop(0.4, '#FFD700')

      // 40% - 60%: 绿色
      grad.addColorStop(0.41, '#90EE90')
      grad.addColorStop(0.6, '#008000')

      // 60% - 80%: 浅紫色 (Light Purple / Violet)
      grad.addColorStop(0.61, '#EE82EE')
      grad.addColorStop(0.8, '#BA55D3')

      // 80% - 100%: 鲜红 -> 深红
      grad.addColorStop(0.81, '#FF0000')
      grad.addColorStop(1.0, '#8B0000')

      ctx.fillStyle = grad
      ctx.fillRect(0, 0, 1, 256)
      this.paletteData = ctx.getImageData(0, 0, 1, 256).data
    },

    // 解析传入的答案值
    parseValue(val) {
      if (!val) {
        this.points = []
        return
      }
      try {
        if (Array.isArray(val)) {
          this.points = val
        } else if (typeof val === 'string') {
          this.points = JSON.parse(val)
        }
      } catch (e) {
        console.warn('解析热力图数据失败:', e)
        this.points = []
      }
      if (this.imgLoaded) {
        this.drawHeatmap()
      }
    },

    onImageLoad() {
      this.imgLoaded = true
      this.resizeCanvas()
      this.cacheBaseImageData() // 缓存底图像素数据
      this.drawHeatmap()
    },

    onResize() {
      if (this.imgLoaded) {
        this.resizeCanvas()
        this.drawHeatmap()
      }
    },

    resizeCanvas() {
      const img = this.$refs.baseImage
      const canvas = this.$refs.canvas
      const tempCanvas = this.$refs.tempCanvas
      
      if (!img || !canvas) return

      // 获取图片实际显示的尺寸
      this.canvasWidth = img.clientWidth
      this.canvasHeight = img.clientHeight
      this.minHeight = this.canvasHeight

      // 设置 Canvas 尺寸匹配图片
      canvas.width = this.canvasWidth
      canvas.height = this.canvasHeight
      
      if (tempCanvas) {
        tempCanvas.width = this.canvasWidth
        tempCanvas.height = this.canvasHeight
      }
    },

    // 缓存底图像素数据（用于白色区域检测）
    cacheBaseImageData() {
      if (!this.blockWhite) return // 未启用则跳过
      
      const img = this.$refs.baseImage
      if (!img) return
      
      try {
        // 创建临时 canvas 绘制图片
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = this.canvasWidth
        tempCanvas.height = this.canvasHeight
        const ctx = tempCanvas.getContext('2d')
        ctx.drawImage(img, 0, 0, this.canvasWidth, this.canvasHeight)
        
        // 获取像素数据
        this.baseImageData = ctx.getImageData(0, 0, this.canvasWidth, this.canvasHeight)
      } catch (e) {
        console.warn('无法获取底图像素数据（可能是跨域图片）:', e)
        this.baseImageData = null
      }
    },

    // 检测指定坐标是否是白色/浅色区域
    isWhiteArea(normX, normY) {
      if (!this.baseImageData) return false
      
      const x = Math.floor(normX * this.canvasWidth)
      const y = Math.floor(normY * this.canvasHeight)
      
      // 边界检查
      if (x < 0 || x >= this.canvasWidth || y < 0 || y >= this.canvasHeight) return true
      
      // 获取像素 RGBA
      const idx = (y * this.canvasWidth + x) * 4
      const r = this.baseImageData.data[idx]
      const g = this.baseImageData.data[idx + 1]
      const b = this.baseImageData.data[idx + 2]
      const a = this.baseImageData.data[idx + 3]
      
      // 透明区域也算白色
      if (a < 128) return true
      
      // RGB 都大于 240 则认为是白色/浅色背景
      const threshold = 240
      return r > threshold && g > threshold && b > threshold
    },

    // 绘制热力图 (核心逻辑)
    drawHeatmap() {
      if (!this.paletteData || !this.imgLoaded || !this.$refs.canvas) return

      const ctx = this.$refs.canvas.getContext('2d')
      const tCtx = this.$refs.tempCanvas.getContext('2d')
      const width = this.canvasWidth
      const height = this.canvasHeight

      // 1. 清空画布
      ctx.clearRect(0, 0, width, height)
      tCtx.clearRect(0, 0, width, height)

      if (this.points.length === 0) return

      // 2. 绘制黑白密度图 (Shadow Map)
      this.points.forEach(p => {
        // 将归一化坐标转换为实际像素坐标
        const x = p.x * width
        const y = p.y * height
        
        tCtx.beginPath()
        const grad = tCtx.createRadialGradient(x, y, this.radius * 0.1, x, y, this.radius)
        grad.addColorStop(0, `rgba(0,0,0,${this.opacity})`)
        grad.addColorStop(1, 'rgba(0,0,0,0)')
        tCtx.fillStyle = grad
        tCtx.arc(x, y, this.radius, 0, Math.PI * 2)
        tCtx.fill()
      })

      // 3. 着色 (Colorization)
      const shadowImg = tCtx.getImageData(0, 0, width, height)
      const shadowData = shadowImg.data
      const colorized = ctx.createImageData(width, height)
      const outputData = colorized.data

      for (let i = 0; i < shadowData.length; i += 4) {
        const alpha = shadowData[i + 3] // 获取密度 (0-255)
        
        if (alpha > 0) {
          const offset = alpha * 4
          // R, G, B 从色谱中取
          outputData[i] = this.paletteData[offset]
          outputData[i + 1] = this.paletteData[offset + 1]
          outputData[i + 2] = this.paletteData[offset + 2]

          // Alpha 处理：低密度区增强显示
          if (alpha < 60) {
            outputData[i + 3] = Math.min(255, alpha * 4)
          } else {
            outputData[i + 3] = 230 // 高密度区
          }
        }
      }
      ctx.putImageData(colorized, 0, 0)
    },

    // 交互事件处理
    getPos(e) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      
      const clientX = e.touches ? e.touches[0].clientX : e.clientX
      const clientY = e.touches ? e.touches[0].clientY : e.clientY

      const x = clientX - rect.left
      const y = clientY - rect.top

      // 返回归一化坐标 (0.0 - 1.0)
      return {
        x: Math.max(0, Math.min(1, x / this.canvasWidth)),
        y: Math.max(0, Math.min(1, y / this.canvasHeight)),
        t: Date.now()
      }
    },

    startDrawing(e) {
      if (this.readonly) return
      this.isDrawing = true
      this.addPoint(e)
    },

    stopDrawing() {
      this.isDrawing = false
    },

    draw(e) {
      if (!this.isDrawing || this.readonly) return
      // 简单节流：如果点过多可以加 requestAnimationFrame 锁
      this.addPoint(e)
    },

    addPoint(e) {
      const p = this.getPos(e)
      
      // 白色区域检测
      if (this.blockWhite && this.isWhiteArea(p.x, p.y)) {
        return // 禁止在白色区域绘制
      }
      
      this.points.push(p)
      
      // 更新视图
      requestAnimationFrame(() => {
        this.drawHeatmap()
      })
      
      // 触发 change 事件更新父组件数据（使用与其他组件一致的格式）
      this.$emit('change', {
        itemKey: this.question.item_key,
        value: JSON.stringify(this.points)
      })
    },

    clearCanvas() {
      this.points = []
      this.drawHeatmap()
      this.$emit('change', {
        itemKey: this.question.item_key,
        value: '[]'
      })
    }
  }
}
</script>

<style scoped>
.heatmap-question {
  width: 100%;
}

.toolbar {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f5f7fa;
  padding: 8px 12px;
  border-radius: 4px;
}

.tool-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tool-group label {
  font-size: 13px;
  color: #606266;
}

.point-count {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}

.canvas-wrapper {
  position: relative;
  width: 100%;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  overflow: hidden;
  user-select: none;
}

.base-image {
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
}

.heatmap-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: crosshair;
}

.heatmap-canvas.readonly {
  cursor: default;
  pointer-events: none;
}

.legend {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #606266;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 8px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  z-index: 10;
  pointer-events: none;
}

.gradient-bar {
  width: 12px;
  height: 80px;
  border-radius: 6px;
  background: linear-gradient(to bottom, #8B0000 0%, #FF0000 20%, #EE82EE 40%, #00FF00 60%, #FFFF00 80%, white 100%);
  border: 1px solid #dcdfe6;
}

.legend-label-top,
.legend-label-bottom {
  font-size: 10px;
  color: #909399;
}
</style>
